/**
 * Attaches CustomEvent 'component-library-analytics' listener to document.body
 * to translate component library actions into analytics dataLayer events.
 */
import _recordEvent from 'platform/monitoring/record-event';

// va-header-navigation's version value for each of its presentations.
const HEADER_VERSIONS = { panel: 'desktop', mobile: 'mobile' };

// The controls nav-header records. The logo, the Menu button, Sign in, Sign
// out, and the back button have events of their own.
const NAV_HEADER_CONTROLS = [
  undefined,
  'contact-us',
  'search',
  'see-all',
  'promo',
];

// The header-level value for a control, matching the areas marked on the
// analytics request (va.gov-team#157216). The mobile menu and the My VA and
// Profile sections follow the component's level.
const headerLevel = ({ surface, level, control }) => {
  if (control === 'promo') return 'hub';
  if (control === 'see-all') return 'link';
  if (surface === 'bar') return 'top';
  if (surface === 'hub') return 'second';
  if (surface === 'panel') return 'link';
  return ['top', 'second', 'link'][level - 1];
};

// Below the desktop layout, the menu's My VA section stands in for the My VA
// pages' side navigation, so it records the side navigation event instead.
const isMobileSidenav = ({ surface, presentation }) =>
  surface === 'personal' && presentation === 'mobile';

const isNavHeader = details =>
  NAV_HEADER_CONTROLS.includes(details.control) && !isMobileSidenav(details);

// The nav-header keys. heading_1 is recorded below the top level, and
// heading_2 on links and the promo card. Open events carry no URL.
const navHeaderDataLayer = action => details => {
  const { label, href, sections = [], presentation } = details;
  const headerLevelValue = headerLevel(details);
  return {
    action,
    'header-click-label': label,
    'header-click-url': action === 'click' ? href : undefined,
    'header-level': headerLevelValue,
    // eslint-disable-next-line camelcase -- the analytics request's key name
    heading_1: headerLevelValue === 'top' ? undefined : sections[0],
    // eslint-disable-next-line camelcase -- the analytics request's key name
    heading_2:
      headerLevelValue === 'link' || headerLevelValue === 'hub'
        ? sections[1]
        : undefined,
    'link-type': 'primary navigation',
    version: HEADER_VERSIONS[presentation],
  };
};

const sidenavDataLayer =
  action =>
  ({ label, href }) => ({
    action,
    'sidenav-click-label': label,
    'sidenav-click-url': action === 'click' ? href : undefined,
    'link-type': 'secondary navigation',
    version: 'mobile',
  });

const versionDataLayer = ({ presentation }) => ({
  version: HEADER_VERSIONS[presentation],
});

const analyticsEvents = {
  Modal: [{ action: 'show', event: 'int-modal-show', prefix: 'modal' }],
  'va-accordion': [
    {
      action: 'expand',
      event: 'int-accordion-expand',
      prefix: 'accordion',
    },
    {
      action: 'collapse',
      event: 'int-accordion-collapse',
      prefix: 'accordion',
    },
  ],
  'va-additional-info': [
    {
      action: 'expand',
      event: 'int-additional-info-expand',
      prefix: 'additional-info',
    },
    {
      action: 'collapse',
      event: 'int-additional-info-collapse',
      prefix: 'additional-info',
    },
  ],
  'va-alert': [
    {
      action: 'linkClick',
      event: 'nav-alert-box-link-click',
      prefix: 'alert-box',
    },
  ],
  'va-alert-expandable': [
    {
      action: 'expand',
      event: 'int-alert-expandable-expand',
      prefix: 'alert-expandable',
    },
    {
      action: 'collapse',
      event: 'int-alert-expandable-collapse',
      prefix: 'alert-expandable',
    },
  ],
  'va-breadcrumbs': [
    {
      action: 'linkClick',
      event: 'nav-breadcrumb-link-click',
      prefix: 'breadcrumbs',
    },
  ],
  'va-banner': [
    {
      action: 'close',
      event: 'int-banner-close',
      prefix: 'banner',
    },
  ],
  'va-button': [
    {
      action: 'click',
      event: 'cta-button-click',
      prefix: 'button',
    },
  ],
  'va-button-pair': [
    {
      action: 'click',
      event: 'int-button-pair-click',
      prefix: 'button-pair',
    },
  ],
  'va-button-segmented': [
    {
      action: 'click',
      event: 'int-button-segmented-click',
      prefix: 'button-segmented',
    },
  ],
  'va-checkbox': [
    {
      action: 'change',
      event: 'int-checkbox-option-click',
      prefix: 'checkbox',
    },
  ],
  'va-checkbox-group': [
    {
      action: 'change',
      event: 'int-checkbox-group-option-click',
      prefix: 'checkbox-group',
    },
  ],
  'va-date': [
    {
      action: 'blur',
      event: 'int-date-blur',
      prefix: 'date',
    },
  ],
  'va-file-input': [
    {
      action: 'change',
      event: 'int-file-input-change',
      prefix: 'file-input',
    },
  ],
  // The header's events carry where the control sits (surface, level,
  // sections), which layout showed (presentation) and, for its built-in
  // controls, an untranslated key (control). Each entry below matches on those
  // and builds the events in the header analytics request
  // (va.gov-team#157216).
  'va-header-navigation': [
    // Collapses record nothing: no entry matches them.
    {
      action: 'expand',
      match: isNavHeader,
      event: 'nav-header',
      dataLayer: navHeaderDataLayer('open'),
    },
    {
      action: 'linkClick',
      match: isNavHeader,
      event: 'nav-header',
      dataLayer: navHeaderDataLayer('click'),
    },
    {
      action: 'expand',
      match: isMobileSidenav,
      event: 'nav-sidenav-mobile',
      dataLayer: sidenavDataLayer('open'),
    },
    {
      action: 'linkClick',
      match: isMobileSidenav,
      event: 'nav-sidenav-mobile',
      dataLayer: sidenavDataLayer('click'),
    },
    {
      action: 'expand',
      match: ({ control }) => control === 'menu',
      event: 'nav-header-menu-expand',
      dataLayer: versionDataLayer,
    },
    {
      action: 'linkClick',
      match: ({ control }) => control === 'logo',
      event: 'nav-header-logo',
      dataLayer: versionDataLayer,
    },
    {
      action: 'click',
      match: ({ control }) => control === 'sign-in',
      event: 'nav-header-sign-in',
      dataLayer: details => ({
        'header-sign-in-action': 'Header - Sign in',
        ...versionDataLayer(details),
      }),
    },
    // Identity's logout event also records from the page's vaSignOutClick
    // listener; this one adds the header's version.
    {
      action: 'linkClick',
      match: ({ control }) => control === 'sign-out',
      event: 'nav-header-sign-out',
      dataLayer: versionDataLayer,
    },
    {
      action: 'click',
      match: ({ control }) => control === 'back',
      event: 'nav-header-back-to-menu',
      dataLayer: versionDataLayer,
    },
  ],
  'va-language-toggle': [
    {
      action: 'linkClick',
      event: 'nav-language-toggle-click',
      prefix: 'language-toggle',
    },
  ],
  'va-link': [
    {
      action: 'click',
      event: 'nav-link-click',
      prefix: 'link',
    },
  ],
  'va-link-action': [
    {
      action: 'click',
      event: 'nav-link-click',
      prefix: 'link-action',
    },
  ],
  'va-loading-indicator': [
    {
      action: 'displayed',
      event: 'loading-indicator-displayed',
      prefix: 'loading-indicator',
    },
  ],
  'va-maintenance-banner': [
    {
      action: 'close',
      event: 'int-maintenance-banner-close',
      prefix: 'maintenance-banner',
    },
  ],
  'va-memorable-date': [
    {
      action: 'blur',
      event: 'int-memorable-date-blur',
      prefix: 'memorable-date',
    },
  ],
  'va-modal': [
    {
      action: 'click',
      event: 'cta-modal-click',
      prefix: 'modal',
    },
    {
      action: 'show',
      event: 'int-modal-show',
      prefix: 'modal',
    },
  ],
  'va-notification': [
    {
      action: 'linkClick',
      event: 'nav-notification-link-click',
      prefix: 'notification',
    },
    {
      action: 'close',
      event: 'int-notification-close',
      prefix: 'notification',
    },
  ],
  'va-number-input': [
    {
      action: 'blur',
      event: 'int-number-input-blur',
      prefix: 'number-input',
    },
  ],
  'va-official-gov-banner': [
    {
      action: 'expand',
      event: 'int-official-gov-banner-expand',
      prefix: 'official-gov-banner',
    },
    {
      action: 'collapse',
      event: 'int-official-gov-banner-collapse',
      prefix: 'official-gov-banner',
    },
  ],
  'va-on-this-page': [
    {
      action: 'click',
      event: 'nav-jumplink-click',
    },
  ],
  'va-privacy-agreement': [
    {
      action: 'click',
      event: 'nav-privacy-agreement-checkbox-click',
      prefix: 'privacy-agreement',
    },
  ],
  'va-promo-banner': [
    {
      action: 'linkClick',
      event: 'nav-promo-banner-link-click',
      prefix: 'promo-banner',
    },
    {
      action: 'close',
      event: 'int-promo-banner-close',
      prefix: 'promo-banner',
    },
  ],
  'va-radio': [
    {
      action: 'change',
      event: 'int-radio-button-option-click',
      prefix: 'radio-button',
    },
  ],
  'va-search-input': [
    {
      action: 'click',
      event: 'int-search-input-click',
      prefix: 'search-input',
    },
    {
      action: 'blur',
      event: 'int-search-input-blur',
      prefix: 'search-input',
    },
  ],
  'va-select': [
    {
      action: 'change',
      event: 'int-select-box-option-click',
      prefix: 'select',
    },
  ],
  'va-pagination': [
    {
      action: 'linkClick',
      event: 'nav-paginate-number',
      prefix: 'pagination',
    },
    {
      action: 'linkClick',
      event: 'nav-paginate-previous',
      prefix: 'pagination',
    },
    {
      action: 'linkClick',
      event: 'nav-paginate-next',
      prefix: 'pagination',
    },
  ],
  'va-progress-bar': [
    {
      action: 'change',
      event: 'nav-progress-bar-change',
      prefix: 'progress-bar',
    },
  ],
  'va-segmented-progress-bar': [
    {
      action: 'change',
      event: 'nav-segmented-progress-bar-change',
      prefix: 'segmented-progress-bar',
    },
  ],
  'va-telephone': [
    {
      action: 'click',
      event: 'int-telephone-link-click',
      prefix: 'telephone',
    },
  ],
  'va-text-input': [
    {
      action: 'blur',
      event: 'int-text-input-blur',
      prefix: 'text-input',
    },
  ],
  'va-textarea': [
    {
      action: 'blur',
      event: 'int-textarea-blur',
      prefix: 'textarea',
    },
  ],
};

// This function assumes an accordion element in a <section> tag
// which has a data-label attribute set.
const getSectionLabel = node => {
  let currentNode = node;
  while (
    currentNode &&
    currentNode.tagName &&
    currentNode.nodeName.toUpperCase() !== 'SECTION'
  ) {
    currentNode = currentNode.parentNode;
  }
  return currentNode?.dataset?.label;
};

export function subscribeComponentAnalyticsEvents(
  e,
  recordEvent = _recordEvent,
) {
  // Is it a component we are tracking?
  const component = analyticsEvents[e.detail.componentName];

  if (component) {
    const { version, details } = e.detail;
    // Entries with a match predicate are chosen by it, and every match
    // records; without one, the first entry for the action records, as before.
    const matched = component.filter(
      ev =>
        ev.action === e.detail.action &&
        typeof ev.match === 'function' &&
        ev.match(details || {}),
    );
    const actions = matched.length
      ? matched
      : [component.find(ev => ev.action === e.detail.action && !ev.match)];

    actions.filter(Boolean).forEach(action => {
      const dataLayer = {
        event: action.event,
        'event-source': 'component-library',
        'component-library-version': version,
      };

      if (action.dataLayer) {
        // The entry builds its own keys from the details.
        Object.assign(dataLayer, action.dataLayer(details || {}));
      } else if (details) {
        for (const key of Object.keys(details)) {
          const newKey = action.prefix ? `${action.prefix}-${key}` : key;

          dataLayer[newKey] = details[key];
        }
      }

      if (
        ['int-accordion-expand', 'int-accordion-collapse'].includes(
          action.event,
        )
      ) {
        dataLayer['accordion-section-label'] = getSectionLabel(e.target);
      }

      recordEvent(dataLayer);

      // Reset dataLayer properties to undefined with a new push
      // so they don't bleed into other event.

      const clearedDataLayer = { ...dataLayer };

      // Remove event property
      if (Object.prototype.hasOwnProperty.call(clearedDataLayer, 'event')) {
        delete clearedDataLayer.event;
      }

      // Remove gtm.uniqueEventId property
      if (
        Object.prototype.hasOwnProperty.call(
          clearedDataLayer,
          'gtm.uniqueEventId',
        )
      ) {
        delete clearedDataLayer['gtm.uniqueEventId'];
      }

      // Set everything else to undefined
      Object.keys(clearedDataLayer).forEach(key => {
        clearedDataLayer[key] = undefined;
      });

      recordEvent(clearedDataLayer);
    });
  }
}

document.body.addEventListener(
  'component-library-analytics',
  subscribeComponentAnalyticsEvents,
);
